import { httpBaseFromWsUrl } from "./auth-api.js";

// Клиент трекера депозитов: gateway проксирует запросы в админ-панель и
// дублирует занесённый депозит в «Мою смену» (расчёт ЗП).
// Значения paymentMethod/status — числовые enum'ы админки; их подписи приходят
// в контексте, чтобы список не разъезжался с бэкендом.

export interface TrackerBindingBot {
  botId: number;
  botName: string;
  kind: number;
}

export interface TrackerBinding {
  geoId: string;
  geoTitle: string;
  /** Основной бот проекта — дефолт для карточек совсем новых клиентов. */
  botId: number;
  botName: string | null;
  workspaceId: number | null;
  workspaceName: string | null;
  currency: string | null;
  /** Все трафик-боты проекта (первый — основной); клиента ищем по всем. */
  bots: TrackerBindingBot[];
}

export interface TrackerOption {
  value: number;
  label: string;
}

export interface TrackerGeoOption {
  geoId: string;
  title: string;
  configured: boolean;
}

export interface TrackerContext {
  enabled: boolean;
  reason: string | null;
  binding: TrackerBinding | null;
  geos: TrackerGeoOption[];
  supportName: string;
  userGeoId: string | null;
  salaryMetric: "deposits" | "total" | null;
  paymentMethods: TrackerOption[];
  statuses: TrackerOption[];
}

export interface TrackerStep {
  stepId: number;
  stepNumber: number;
  amount: number;
  profitAmount: number;
  paymentMethod: number;
  supportName: string | null;
  createdAt: string;
}

export interface TrackerClient {
  clientId: number;
  botId: number | null;
  botName: string | null;
  telegramId: number | null;
  username: string | null;
  nickname: string | null;
  fullName: string | null;
  siteLogin: string | null;
  contact: string | null;
  age: number | null;
  country: string | null;
  city: string | null;
  sessionHours: number | null;
  status: number;
  totalSteps: number;
  totalInvestments: number;
  projectBalance: number;
  firstLineSupportName: string | null;
  secondLineSupportName: string | null;
  deeplinkTag: string | null;
  hasFirstDeposit: boolean;
  nextStepNumber: number;
  lastProfitAmount: number | null;
  lastPaymentMethod: number | null;
  lastDepositAt: string | null;
  steps: TrackerStep[];
}

export interface TrackerWriteResult {
  ok: boolean;
  duplicate?: boolean;
  client?: TrackerClient | null;
  message?: string | null;
  /** Запись, попавшая в «Мою смену» для расчёта ЗП; null — линия не назначена. */
  localEntry?: { entryId: string; amount: number } | null;
}

export interface TrackerCreateClientPayload {
  externalOperationId: string;
  geoId?: string | null;
  /** Бот новой карточки из ботов проекта; null — основной бот проекта. */
  botId?: number | null;
  telegramId?: number | null;
  siteLogin?: string | null;
  nickname?: string | null;
  username?: string | null;
  contact?: string | null;
  supportName?: string | null;
  paymentMethod: number;
  amount: number;
  depositedAt?: string | null;
  age?: number | null;
  sessionHours?: number | null;
  country?: string | null;
  city?: string | null;
}

export interface TrackerDepositPayload {
  externalOperationId: string;
  geoId?: string | null;
  stepNumber: number;
  paymentMethod: number;
  amount: number;
  supportName?: string | null;
  depositedAt?: string | null;
}

export interface TrackerEditPayload {
  externalOperationId: string;
  geoId?: string | null;
  telegramId?: number | null;
  siteLogin?: string | null;
  nickname?: string | null;
  username?: string | null;
  contact?: string | null;
  supportName?: string | null;
  age?: number | null;
  sessionHours?: number | null;
  country?: string | null;
  city?: string | null;
  status?: number | null;
}

export interface TrackerStepPayload {
  externalOperationId: string;
  geoId?: string | null;
  stepNumber: number;
  paymentMethod: number;
  amount: number;
  profitAmount?: number | null;
  supportName?: string | null;
  depositedAt?: string | null;
}

const request = async <TResponse>(url: string, init?: RequestInit): Promise<TResponse> => {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `Запрос не прошёл (${response.status})`);
  }
  return (await response.json()) as TResponse;
};

const postJson = <TResponse>(url: string, body: unknown): Promise<TResponse> =>
  request<TResponse>(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body)
  });

/**
 * Резервный путь: депозит только в «Мою смену» (расчёт ЗП), без карточки клиента.
 * Нужен, когда админ-панель недоступна — иначе смена саппорта потеряла бы депозит.
 */
export const recordShiftOnlyDeposit = (
  wsUrl: string,
  amount: number
): Promise<{ entry: { entryId: string; amount: number; enteredAt: string } }> => {
  const base = httpBaseFromWsUrl(wsUrl);
  return postJson(`${base}/api/support/deposits`, { amount });
};

export const trackerApi = {
  getContext(wsUrl: string, geoId?: string | null): Promise<TrackerContext> {
    const base = httpBaseFromWsUrl(wsUrl);
    const query = geoId ? `?geoId=${encodeURIComponent(geoId)}` : "";
    return request<TrackerContext>(`${base}/api/tracker/context${query}`);
  },
  resolve(
    wsUrl: string,
    input: { telegramId?: number | null; username?: string | null; geoId?: string | null }
  ): Promise<{ success: boolean; message: string; matches: TrackerClient[] }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/resolve`, input);
  },
  createClient(wsUrl: string, payload: TrackerCreateClientPayload): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients`, payload);
  },
  addDeposit(
    wsUrl: string,
    clientId: number,
    payload: TrackerDepositPayload
  ): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients/${clientId}/deposits`, payload);
  },
  updateProfit(
    wsUrl: string,
    clientId: number,
    payload: { externalOperationId: string; geoId?: string | null; profitAmount: number }
  ): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients/${clientId}/profit`, payload);
  },
  updateStatus(
    wsUrl: string,
    clientId: number,
    payload: { externalOperationId: string; geoId?: string | null; status: number }
  ): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients/${clientId}/status`, payload);
  },
  editClient(
    wsUrl: string,
    clientId: number,
    payload: TrackerEditPayload
  ): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients/${clientId}/edit`, payload);
  },
  updateStep(
    wsUrl: string,
    clientId: number,
    stepId: number,
    payload: TrackerStepPayload
  ): Promise<TrackerWriteResult> {
    const base = httpBaseFromWsUrl(wsUrl);
    return postJson(`${base}/api/tracker/clients/${clientId}/steps/${stepId}`, payload);
  }
};

// --- Настройка овнера: боты админ-панели и маппинг гео → бот -----------------

export interface TrackerAdminBot {
  botId: number;
  name: string;
  slug: string;
  /** BotKind админки: 0 user-бот, 1 трекер сотрудников, 6 Ютик… */
  kind: number;
  currency: string;
  geoCode: string;
  workspaceId: number;
  workspaceName: string | null;
  workspaceGeoCode: string | null;
  workspaceCurrency: string | null;
  isActive: boolean;
}

export interface TrackerGeoMapping {
  geoId: string;
  botId: number;
  botName: string | null;
  workspaceId: number | null;
  currency: string | null;
  updatedAt: string;
}

/**
 * Служебные BotKind админки (трекер сотрудников, SMM-канал, новости, уникализатор,
 * вакансии) — трафика и карточек клиентов в них нет. Зеркало SERVICE_BOT_KINDS
 * в gateway/integrations/tracker-api.ts.
 */
export const SERVICE_BOT_KINDS: ReadonlySet<number> = new Set([1, 2, 8, 9, 11]);

export const isTrafficBot = (bot: Pick<TrackerAdminBot, "kind">): boolean =>
  !SERVICE_BOT_KINDS.has(bot.kind);

export interface TrackerProjectCandidate {
  workspaceId: number;
  name: string;
  geoCode: string | null;
  currency: string | null;
  /** Проект выключен в админке: в список выбора не попадает, но выбранный вручную показываем с пометкой. */
  isActive?: boolean;
  trafficBots: number;
}

/** Сопоставление гео с проектом: manual — выбор овнера, auto — единственный проект с кодом страны, ambiguous — несколько, none — ни одного. */
export interface TrackerGeoStatus {
  geoId: string;
  title: string;
  mode: "manual" | "auto" | "ambiguous" | "none";
  code: string | null;
  project: TrackerProjectCandidate | null;
  candidates: TrackerProjectCandidate[];
}

export interface TrackerAdminBotsResponse {
  bots: TrackerAdminBot[];
  mappings: TrackerGeoMapping[];
  geoStatus: TrackerGeoStatus[];
}

export const trackerAdminApi = {
  listBots(wsUrl: string): Promise<TrackerAdminBotsResponse> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<TrackerAdminBotsResponse>(`${base}/api/admin/tracker/bots`);
  },
  /** Привязать гео к проекту админки; null снимает привязку. Основной бот gateway выберет сам. */
  setGeoProject(
    wsUrl: string,
    geoId: string,
    workspaceId: number | null
  ): Promise<{ mapping: TrackerGeoMapping | null }> {
    const base = httpBaseFromWsUrl(wsUrl);
    return request<{ mapping: TrackerGeoMapping | null }>(`${base}/api/admin/tracker/geo-bots`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ geoId, workspaceId })
    });
  }
};
